import { isNullOrUndefined, RxEventEmitter } from '@bimser/core';
import IconCloseWindow from '@bimser/icons/16/close-window';
import IconPanelSize from '@bimser/icons/16/panel-size';
import IconPanelBig from '@bimser/icons/32/panel-big';
import IconPanelMiddle from '@bimser/icons/32/panel-middle';
import IconPanelSmall from '@bimser/icons/32/panel-small';
import classNames from 'classnames/bind';
import React from 'react';
import { Subscription } from 'rxjs';
import {
	BCButton,
	BCButtonOverflow,
	BCPopover,
	BCSkeleton,
	ContentType,
	findMessage,
	FlexPanelActionTypes,
	IButtonOverflowItem
} from '..';
import BCScrollbars from '../BCScrollbars';
import Style from './assets/css/style.scss';
import { IFlexPanelClickEventArgs, IFlexPanelProps, IMessageReceivedEventArgs } from './entities';
import IFlexPanelComponent from './entities/IFlexPanelComponent';

const cx = classNames.bind(Style);

const overflowButtonProps = { type: 'text' as const };

const FlexPanel = (props: IFlexPanelProps) => {
	const [headerEventsProps, setHeaderEventsProps] = React.useState<IButtonOverflowItem[]>([]);
	const [customHeader, setCustomHeader] = React.useState<React.ReactNode>(null);
	const [hideTitle, setHideTitle] = React.useState<boolean>(props.isTitleHide);
	const [sizePopoverVisible, setSizePopoverVisible] = React.useState<boolean>(false);

	const rxSubscriptionRef = React.useRef<Subscription>(null);
	const wrapperRef = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		RxEventEmitter.listen(props.id, {
			next: rxEventHandler
		}).then(subscription => {
			rxSubscriptionRef.current = subscription;
		});

		document.addEventListener('mousedown', onClickPopoverOutside);

		return () => {
			rxSubscriptionRef.current?.unsubscribe();
			document.removeEventListener('mousedown', onClickPopoverOutside);
		};
	}, []);

	React.useEffect(() => {
		setTimeout(() => {
			RxEventEmitter.emit(FlexPanelActionTypes.DIMENSIONS_UPDATED, null);
			window.dispatchEvent(new Event('resize'));
		}, 500);
	}, [props.size]);

	const rxEventHandler = (data: IMessageReceivedEventArgs) => {
		if (data.type == FlexPanelActionTypes.SET_HEADER_EVENTS) {
			headerEventsReceiver(data.data);
		} else if (data.type == FlexPanelActionTypes.SET_CUSTOM_HEADER) {
			customHeaderReceiver(data.data);
		} else if (data.type == FlexPanelActionTypes.SET_TITLE_VISIBILITY) {
			setTitleVisReceiver(data.data);
		}
	};

	const setTitleVisReceiver = (data: any) => {
		setHideTitle(!data);
	};

	const customHeaderReceiver = (data: any) => {
		setCustomHeader(data);
	};

	const headerEventsReceiver = (data: { items: IButtonOverflowItem[] }) => {
		if (!props.fromMobile) {
			setHeaderEventsProps(data.items || []);
		}
	};

	const onClickPopoverOutside = (event: any) => {
		if (wrapperRef?.current && !wrapperRef.current.contains(event.target)) {
			setSizePopoverVisible(false);
		}
	};

	const renderTitle = () => {
		if (hideTitle) return null;
		return (
			<BCSkeleton
				className={Style.viewerSkeconston}
				contentType={ContentType.Title}
				loadingStatus={isNullOrUndefined(props.title)}
			>
				<div className={Style.title}>
					<span>{props.title}</span>
					{props.subtitle && <span className={Style.subtitle}>- {props.subtitle}</span>}
				</div>
			</BCSkeleton>
		);
	};

	const onChangePopoverVisible = () => {
		setSizePopoverVisible(!sizePopoverVisible);
	};

	const onClickPanelSize = (args: any, size: number) => {
		onMaximizeClick({ data: { ...props, size: size }, senderArgs: args });
		setSizePopoverVisible(false);
	};

	const renderPanelResizeButton = () => {
		if (props.isMaximizeButtonHide) return null;

		return (
			<BCPopover
				title={findMessage.get('101787')}
				open={sizePopoverVisible}
				content={
					<div
						ref={wrapperRef}
						className={Style.popoverContentStyles}
					>
						<div
							className={cx({
								popoverIconContainer: true,
								popoverIconSelected: props.size === 1
							})}
							onClick={args => onClickPanelSize(args, 1)}
						>
							<IconPanelSmall />
						</div>
						<div
							className={cx({
								popoverIconContainer: true,
								popoverIconSelected: props.size === 2
							})}
							onClick={args => onClickPanelSize(args, 2)}
						>
							<IconPanelMiddle />
						</div>
						<div
							className={cx({
								popoverIconContainer: true,
								popoverIconSelected: props.size === 3
							})}
							onClick={args => onClickPanelSize(args, 3)}
						>
							<IconPanelBig />
						</div>
					</div>
				}
				placement="bottomRight"
			>
				<BCButton
					onClick={onChangePopoverVisible}
					icon={<IconPanelSize />}
					type="text"
				/>
			</BCPopover>
		);
	};

	const renderCloseButton = () => {
		if (props.isCloseButtonHide) return null;

		return (
			<BCButton
				onClick={args => onCloseClick({ data: props, senderArgs: args.event })}
				icon={<IconCloseWindow />}
				type="text"
			/>
		);
	};

	const renderPanelHeaderActions = () => {
		if (props.isHeaderActionsHide) return null;
		return (
			<div className={Style.actions}>
				{renderPanelResizeButton()}
				{renderCloseButton()}
			</div>
		);
	};

	const renderCustomEvents = () => {
		return (
			<div className={cx({ events: true, fixedSize: !!customHeader })}>
				{headerEventsProps.length ? (
					<div
						className={cx({ eventButtons: true, divider: !props.isHeaderActionsHide })}
					>
						<BCButtonOverflow
							items={headerEventsProps}
							overflowButtonProps={overflowButtonProps}
							alignRight
						/>
					</div>
				) : null}
				{renderPanelHeaderActions()}
			</div>
		);
	};

	const renderHeader = () => {
		if (props.isHeaderAreaHide || props.isStartupPanel) return null;
		return (
			<div
				className={cx({
					header: true,
					contentWithHiddenHeader: props.isHeaderAreaHide || props.isStartupPanel
				})}
			>
				{renderTitle()}
				{customHeader}
				{renderCustomEvents()}
			</div>
		);
	};

	const renderScrollbars = (children: React.ReactElement) => {
		return <BCScrollbars cssClass={Style.contentScroll}>{children}</BCScrollbars>;
	};

	const renderComponents = () => {
		return props.components.map((Component: IFlexPanelComponent) => (
			<div
				className={cx({
					content: true,
					fromMobile: props.fromMobile,
					contentWithHiddenHeader: props.isHeaderAreaHide || props.isStartupPanel
				})}
				key={Component.props.panelGuid}
			>
				{!props.hideScrollbar ? (
					renderScrollbars(<Component.component {...Component.props} />)
				) : (
					<Component.component {...Component.props} />
				)}
			</div>
		));
	};

	const onPanelClick = (args: IFlexPanelClickEventArgs) => {
		args.senderArgs.persist();
		props.onPanelClick?.(args);
	};

	const onMaximizeClick = (args: IFlexPanelClickEventArgs) => {
		args.senderArgs.persist();
		args.senderArgs.stopPropagation();
		props.onMaximizeClick?.(args);
	};

	const onAnimationEnd = (e: React.AnimationEvent<HTMLDivElement>) => {
		props.onAnimationEnd?.({
			panel: props,
			animationName: e.animationName,
			elapsedTime: e.elapsedTime
		});
	};

	const onTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
		if (e.currentTarget === e.target && e.propertyName === 'width') {
			props.onAnimationEnd?.({
				panel: props,
				animationName: e.propertyName,
				elapsedTime: e.elapsedTime
			});
		}
	};

	const onCloseClick = (args: IFlexPanelClickEventArgs) => {
		args.senderArgs.persist();
		args.senderArgs.stopPropagation();

		const propPromises: Array<Promise<any>> = [];

		props.components.forEach((component: IFlexPanelComponent, index) => {
			if (
				component.props &&
				component.props.onBeforePanelClose &&
				typeof component.props.onBeforePanelClose === 'function'
			) {
				const componentPropsClosePromise = component.props.onBeforePanelClose(args);
				if (componentPropsClosePromise !== undefined) {
					propPromises.push(componentPropsClosePromise);
				}
			}
		});

		Promise.all(propPromises).then(() => {
			props.onCloseClick?.(args);
		});
	};

	return (
		<div
			className={cx({
				flexPanel: true,
				active: props.isActive,
				['activeFlexPanel']: props.isActive,
				small: props.size === 1 || (props.size !== 2 && props.size !== 3),
				medium: props.size === 2,
				large: props.size === 3,
				[props.animationCssClass]: true,
				startupPanel: props.isStartupPanel || props.isTransparentBackground,
				ghostPanel: props.ghostPanel,
				fromMobile: props.fromMobile
			})}
			onClick={args => onPanelClick({ data: props, senderArgs: args })}
			onAnimationEnd={onAnimationEnd}
			onTransitionEnd={onTransitionEnd}
			id={props.id}
			data-flex-panel
		>
			{renderHeader()}
			{renderComponents()}
		</div>
	);
};

export default FlexPanel;

export { IFlexPanelClickEventArgs, IFlexPanelProps };
